// Shared compact "Approvals" tile (Pending / Rejected) with drill-down modals. The pending-chain
// logic (transaction type -> approver role -> records) and the rejected-transactions fetch are
// the same ones DashboardContent.jsx uses inline for its two big stat buttons — this pulls that
// into a reusable card for dashboards that want the compact combined-tile layout instead.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import {
    Clock, AlertTriangle, ChevronRight, X, Loader2, ListChecks, LayoutList, CheckCircle,
} from 'lucide-react';
import { API_BASE_URL } from '../../config/apiConfig';
import { formatCurrency } from '../../utilities/formatCurrency';
import {
    fetchTrackingValues,
    fetchTrackingNos,
    fetchTrackingData,
    setSelectedMoid,
    setSelectedRole,
    clearTrackingDetail,
    selectTrackingValues,
    selectTrackingNos,
    selectTrackingData,
    selectSelectedMoid,
    selectSelectedRoleId,
    selectSelectedRoleName,
    selectTrackingValuesLoading,
    selectTrackingNosLoading,
    selectTrackingDataLoading,
} from '../../slices/dashboardSlice/pendingTrackingSlice';

const ApprovalsCard = ({ roleId, userId, ccCodes = '', groupId = 0 }) => {
    const dispatch = useDispatch();

    const [rejectedData, setRejectedData] = useState([]);
    const [rejectedLoading, setRejectedLoading] = useState(false);
    const [showRejectedModal, setShowRejectedModal] = useState(false);
    const [showPendingModal, setShowPendingModal] = useState(false);

    const trackingValues = useSelector(selectTrackingValues);
    const trackingNos = useSelector(selectTrackingNos);
    const trackingData = useSelector(selectTrackingData);
    const selectedMoid = useSelector(selectSelectedMoid);
    const selectedRoleId = useSelector(selectSelectedRoleId);
    const selectedRoleName = useSelector(selectSelectedRoleName);
    const trackingValuesLoading = useSelector(selectTrackingValuesLoading);
    const trackingNosLoading = useSelector(selectTrackingNosLoading);
    const trackingDataLoading = useSelector(selectTrackingDataLoading);

    useEffect(() => {
        if (roleId) dispatch(fetchTrackingValues(roleId));
    }, [dispatch, roleId]);

    useEffect(() => {
        if (!userId || !roleId) return;
        setRejectedLoading(true);
        axios.get(`${API_BASE_URL}/Accounts/GetRejectedData`, { params: { UserId: userId, RoleId: roleId } })
            .then(res => setRejectedData(res.data?.Data || []))
            .catch(() => setRejectedData([]))
            .finally(() => setRejectedLoading(false));
    }, [userId, roleId]);

    // Step 1 — pick transaction type: fetch approval chain only
    const handleSelectTrackingType = (moid) => {
        dispatch(setSelectedMoid(moid));
        dispatch(fetchTrackingNos({ roleId, moid, ccCodes, userId }));
    };

    // Step 2 — pick a role in the approval chain: fetch that role's pending data
    const handleSelectApprovalRole = (nos) => {
        if (nos.No === 0) return; // no pending items for this role
        dispatch(setSelectedRole({ roleId: nos.UserRoleID, roleName: nos.UserRoleCode }));
        dispatch(fetchTrackingData({ roleId: nos.UserRoleID, moid: selectedMoid, ccCodes, groupId }));
    };

    const handleClosePendingModal = () => {
        setShowPendingModal(false);
        dispatch(clearTrackingDetail());
    };

    const pendingCount = trackingValues.length;
    const rejectedCount = rejectedData.length;

    return (
        <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 h-full flex flex-col">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Approvals</h3>

                <div className="space-y-2 flex-1">
                    <button
                        onClick={() => pendingCount > 0 && setShowPendingModal(true)}
                        disabled={pendingCount === 0}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-all text-left ${pendingCount > 0
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-900/30 hover:border-orange-300 dark:hover:border-orange-700 cursor-pointer'
                            : 'bg-gray-50 dark:bg-gray-900/20 border-gray-100 dark:border-gray-700 cursor-default'
                            }`}
                    >
                        <span className="flex items-center gap-2 min-w-0">
                            <Clock className={`w-4 h-4 flex-shrink-0 ${pendingCount > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Pending</span>
                        </span>
                        {trackingValuesLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />
                        ) : pendingCount > 0 ? (
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                    {pendingCount}
                                </span>
                                <span className="text-xs font-semibold text-orange-600 dark:text-orange-400">Review →</span>
                            </span>
                        ) : (
                            <span className="text-xs font-medium text-green-600 dark:text-green-500 flex-shrink-0">All clear</span>
                        )}
                    </button>

                    <button
                        onClick={() => rejectedCount > 0 && setShowRejectedModal(true)}
                        disabled={rejectedCount === 0}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-all text-left ${rejectedCount > 0
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900/30 hover:border-red-300 dark:hover:border-red-700 cursor-pointer'
                            : 'bg-gray-50 dark:bg-gray-900/20 border-gray-100 dark:border-gray-700 cursor-default'
                            }`}
                    >
                        <span className="flex items-center gap-2 min-w-0">
                            <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${rejectedCount > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Rejected</span>
                        </span>
                        {rejectedLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />
                        ) : rejectedCount > 0 ? (
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                    {rejectedCount}
                                </span>
                                <span className="text-xs font-semibold text-red-600 dark:text-red-400">Review →</span>
                            </span>
                        ) : (
                            <span className="text-xs font-medium text-green-600 dark:text-green-500 flex-shrink-0">All clear</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Pending Transactions Modal */}
            {showPendingModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClosePendingModal} />
                    <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-6xl max-h-[90vh] flex flex-col">

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-orange-50 dark:bg-orange-900/20 rounded-t-2xl flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">Pending Transactions</h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{trackingValues.length} transaction type{trackingValues.length !== 1 ? 's' : ''} pending approval</p>
                                </div>
                            </div>
                            <button onClick={handleClosePendingModal}
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body — 2 columns */}
                        <div className="flex flex-1 min-h-0 overflow-hidden">

                            {/* Left: Transaction type list */}
                            <div className="w-64 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 flex flex-col">
                                <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                                    <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                                        <LayoutList className="w-3.5 h-3.5" /> Transaction Types
                                    </p>
                                </div>
                                <div className="overflow-y-auto flex-1 py-1">
                                    {trackingValues.map((item) => {
                                        const label = (item.Value || '').trim();
                                        const active = selectedMoid === item.MOID;
                                        return (
                                            <button
                                                key={item.MOID}
                                                onClick={() => handleSelectTrackingType(item.MOID)}
                                                className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-xs transition-colors
                                                    ${active
                                                        ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 font-semibold border-r-2 border-orange-500'
                                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                                            >
                                                <ListChecks className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-orange-500' : 'text-gray-400'}`} />
                                                <span className="leading-tight">{label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right: Details panel */}
                            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                                {/* Step prompt: nothing selected yet */}
                                {!selectedMoid ? (
                                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 gap-3">
                                        <Clock className="w-12 h-12 opacity-20" />
                                        <p className="text-sm font-medium">Step 1 — Select a transaction type on the left</p>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col overflow-hidden">

                                        {/* Approval Flow — Step 2 */}
                                        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 flex-shrink-0">
                                            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2.5">
                                                Step 2 — Click a role with pending items to view transactions
                                            </p>
                                            {trackingNosLoading ? (
                                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading approval chain…
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {trackingNos.map((nos, idx) => {
                                                        const hasPending = nos.No > 0;
                                                        const isSelected = selectedRoleId === nos.UserRoleID;
                                                        return (
                                                            <div key={nos.UserRoleID} className="flex items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectApprovalRole(nos)}
                                                                    disabled={!hasPending}
                                                                    title={hasPending ? `Click to view ${nos.No} pending items for ${nos.UserRoleCode}` : `No pending items for ${nos.UserRoleCode}`}
                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
                                                                        ${isSelected
                                                                            ? 'bg-orange-500 text-white border-orange-500 shadow-md scale-105'
                                                                            : hasPending
                                                                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 shadow-sm hover:bg-orange-200 dark:hover:bg-orange-900/50 cursor-pointer'
                                                                                : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-60'}`}
                                                                >
                                                                    <span>{nos.UserRoleCode}</span>
                                                                    {hasPending && (
                                                                        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${isSelected ? 'bg-white text-orange-600' : 'bg-orange-500 text-white'}`}>
                                                                            {nos.No}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                                {idx < trackingNos.length - 1 && (
                                                                    <ChevronRight className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Transaction Table — Step 3 */}
                                        <div className="flex-1 overflow-auto">
                                            {!selectedRoleId ? (
                                                <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-2 py-12">
                                                    <ChevronRight className="w-8 h-8 opacity-20" />
                                                    <p className="text-sm">Step 3 — Click an orange role pill above to see its pending transactions</p>
                                                </div>
                                            ) : trackingDataLoading ? (
                                                <div className="flex items-center justify-center h-32 gap-2 text-gray-400">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-sm">Loading transactions for <strong>{selectedRoleName}</strong>…</span>
                                                </div>
                                            ) : trackingData.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500 gap-2">
                                                    <CheckCircle className="w-8 h-8 opacity-30" />
                                                    <p className="text-sm">No pending transactions found for <strong>{selectedRoleName}</strong></p>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Sub-header showing which role's data is shown */}
                                                    <div className="px-4 py-2 bg-orange-50 dark:bg-orange-900/10 border-b border-orange-100 dark:border-orange-900/30 flex items-center gap-2">
                                                        <span className="text-xs font-bold text-orange-700 dark:text-orange-300">
                                                            Pending at: {selectedRoleName}
                                                        </span>
                                                        <span className="text-[10px] text-orange-500 bg-orange-100 dark:bg-orange-900/30 px-1.5 py-0.5 rounded-full font-semibold">
                                                            {trackingData.length} record{trackingData.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                    <table className="w-full text-sm">
                                                        <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/70 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left">#</th>
                                                                <th className="px-4 py-3 text-left">Date</th>
                                                                <th className="px-4 py-3 text-left">CC Code</th>
                                                                <th className="px-4 py-3 text-left">Transaction No</th>
                                                                <th className="px-4 py-3 text-left">Name</th>
                                                                <th className="px-4 py-3 text-right">Amount (₹)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                            {trackingData.map((row, idx) => (
                                                                <tr key={idx} className={`transition-colors hover:bg-orange-50/40 dark:hover:bg-orange-900/10 ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/50'}`}>
                                                                    <td className="px-4 py-2.5 text-gray-400 text-xs">{idx + 1}</td>
                                                                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">{row.Date || '—'}</td>
                                                                    <td className="px-4 py-2.5">
                                                                        <span className="text-xs font-mono font-bold text-[#0d1b5e] dark:text-blue-300 bg-[#0d1b5e]/8 dark:bg-[#0d1b5e]/30 px-2 py-0.5 rounded">
                                                                            {row.CCCode || '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-300">{row.TransactionNo || '—'}</td>
                                                                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400 max-w-[160px]">
                                                                        <span className="truncate block" title={row.Name}>{row.Name || '—'}</span>
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-right font-bold text-gray-800 dark:text-gray-200 text-xs whitespace-nowrap">
                                                                        {formatCurrency(row.Amount)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-gray-50 dark:bg-gray-900/50 text-xs font-bold">
                                                            <tr>
                                                                <td colSpan={5} className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">Total</td>
                                                                <td className="px-4 py-2.5 text-right text-orange-600 dark:text-orange-400">
                                                                    {formatCurrency(trackingData.reduce((s, r) => s + (r.Amount || 0), 0))}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between rounded-b-2xl bg-gray-50/60 dark:bg-gray-900/40 flex-shrink-0">
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                {selectedRoleName && trackingData.length > 0
                                    ? `${trackingData.length} record${trackingData.length !== 1 ? 's' : ''} pending at ${selectedRoleName}`
                                    : ''}
                            </p>
                            <button onClick={handleClosePendingModal}
                                className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejected Transactions Modal */}
            {showRejectedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectedModal(false)} />

                    {/* Modal */}
                    <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[85vh] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-red-50 dark:bg-red-900/20 rounded-t-2xl">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">Rejected Transactions</h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{rejectedData.length} record{rejectedData.length !== 1 ? 's' : ''} found</p>
                                </div>
                            </div>
                            <button onClick={() => setShowRejectedModal(false)}
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-auto flex-1">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/60 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    <tr>
                                        <th className="px-4 py-3 text-left">#</th>
                                        <th className="px-4 py-3 text-left">Ref No</th>
                                        <th className="px-4 py-3 text-left">Module</th>
                                        <th className="px-4 py-3 text-left">CC</th>
                                        <th className="px-4 py-3 text-left">Rejected By</th>
                                        <th className="px-4 py-3 text-left">Date</th>
                                        <th className="px-4 py-3 text-left">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {rejectedData.map((row, idx) => {
                                        const [, moduleName] = (row.MCode || '').split(',');
                                        const [, ccName] = (row.CCName || '').split(',');
                                        return (
                                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                                <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs font-mono font-bold text-[#0d1b5e] dark:text-orange-300 bg-[#0d1b5e]/8 dark:bg-[#0d1b5e]/30 px-2 py-0.5 rounded">
                                                        {row.Refno || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[160px]">
                                                    <span className="text-xs">{(moduleName || row.MCode || '—').trim()}</span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                                                    {(ccName || row.CCName || '—').trim()}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-xs font-semibold text-rose-600 dark:text-rose-400">
                                                        {(row.RejectedBy || '—').trim()}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                                    {row.Rejectedate || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-xs max-w-[200px]">
                                                    <span title={row.Remarks} className="line-clamp-2 block">
                                                        {row.Remarks || '—'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end rounded-b-2xl bg-gray-50/60 dark:bg-gray-900/40">
                            <button onClick={() => setShowRejectedModal(false)}
                                className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ApprovalsCard;
